/**
 * PodTube App - Main entry point and orchestrator
 */

import { store, POPULAR_SUGGESTIONS } from "./store.js";
import { feedService } from "./feedService.js";
import { player } from "./player.js";
import {
  renderChannelChips,
  filterItems,
  renderEpisodeGrid,
  updatePlayerDock,
  updateFilterDropdowns,
  updateActiveCardState,
  showToast,
} from "./ui.js";

let allEpisodes = [];
let deferredInstallPrompt = null;
let searchQuery = "";

// Al cambiar de episodio el reproductor pasa un instante por pausa: se espera a que siga parado.
const IDLE_BEFORE_RELOAD_MS = 3000;

function reloadWhenPlaybackStops() {
  if (!player.isPlaying) {
    window.location.reload();
    return;
  }
  showToast("Nueva versión lista: se aplicará cuando pares la reproducción", "info");
  let idleTimer = null;
  player.subscribe(() => {
    if (player.isPlaying) {
      clearTimeout(idleTimer);
      idleTimer = null;
    } else if (!idleTimer) {
      idleTimer = setTimeout(() => window.location.reload(), IDLE_BEFORE_RELOAD_MS);
    }
  });
}

/**
 * Initialize Service Worker for PWA support with auto-update & reload
 */
function initServiceWorker() {
  if (!("serviceWorker" in navigator)) return;

  let isRefreshing = false;
  const reloadForUpdate = () => {
    if (isRefreshing) return;
    isRefreshing = true;
    reloadWhenPlaybackStops();
  };

  // Whenever the active service worker controller changes, reload to consume fresh assets
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    console.log("[PodTube] Nuevo Service Worker activado. Recargando app...");
    reloadForUpdate();
  });

  // Listen for explicit message from the activated SW
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "SW_UPDATED") {
      console.log("[PodTube] Nueva versión detectada:", event.data.cacheName);
      reloadForUpdate();
    }
  });

  navigator.serviceWorker
    .register("./sw.js")
    .then((reg) => {
      // 1. Check for SW updates on initial load
      reg.update().catch(() => {});

      // 2. Check for updates periodically every 60s
      setInterval(() => {
        reg.update().catch(() => {});
      }, 60 * 1000);

      // 3. Check for updates when user returns to tab or window gains focus
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          reg.update().catch(() => {});
        }
      });
      window.addEventListener("focus", () => {
        reg.update().catch(() => {});
      });

      // 4. When an updated worker is found and installed, activate it immediately
      reg.addEventListener("updatefound", () => {
        const newWorker = reg.installing;
        if (!newWorker) return;

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            console.log("[PodTube] Nueva versión lista para activar");
            newWorker.postMessage({ type: "SKIP_WAITING" });
            showToast("Nueva versión detectada. Actualizando aplicación...", "info");
          }
        });
      });
    })
    .catch((err) => {
      console.warn("Service Worker registration skipped or failed:", err);
    });

  // Check version.json: purge stale local feed cache and update SW on new deploy
  fetch("./version.json?t=" + Date.now())
    .then((r) => (r.ok ? r.json() : null))
    .then((info) => {
      if (!info?.version) return;
      const current = localStorage.getItem("podtube_installed_version");
      if (current && current !== info.version) {
        console.log("[PodTube] Nueva versión detectada en servidor:", info.version);
        localStorage.setItem("podtube_installed_version", info.version);
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith("feed_")) {
            localStorage.removeItem(key);
          }
        }
        navigator.serviceWorker?.getRegistration().then((reg) => reg?.update().catch(() => {}));
      } else {
        localStorage.setItem("podtube_installed_version", info.version);
      }
    })
    .catch(() => {});

  // PWA Install prompt listener
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    const installBtn = document.getElementById("btn-install-pwa");
    if (installBtn) {
      installBtn.classList.remove("hidden");
    }
  });

  const installBtn = document.getElementById("btn-install-pwa");
  if (installBtn) {
    installBtn.onclick = async () => {
      if (deferredInstallPrompt) {
        deferredInstallPrompt.prompt();
        const { outcome } = await deferredInstallPrompt.userChoice;
        if (outcome === "accepted") {
          showToast("¡PodTube instalado en Brave!", "success");
        }
        deferredInstallPrompt = null;
        installBtn.classList.add("hidden");
      }
    };
  }
}

/**
 * Refresh feeds from YouTube
 */
async function loadFeeds(force = false) {
  const loadingIndicator = document.getElementById("loading-indicator");
  const refreshBtn = document.getElementById("btn-refresh-feed");

  if (loadingIndicator) loadingIndicator.classList.remove("hidden");
  if (refreshBtn) refreshBtn.classList.add("spinning");

  try {
    const channels = store.getSubscriptions();
    allEpisodes = await feedService.fetchAllFeeds(channels, force);
    applyFiltersAndRender();
    updateSyncLabel();
  } catch (err) {
    console.error("Error loading feeds:", err);
    showToast("Error cargando los canales. Mostrando copia guardada.", "error");
  } finally {
    if (loadingIndicator) loadingIndicator.classList.add("hidden");
    if (refreshBtn) refreshBtn.classList.remove("spinning");
  }
}

/**
 * Update the "last sync" label in the header
 */
function updateSyncLabel() {
  const label = document.getElementById("last-sync-label");
  if (!label) return;
  const ts = feedService.lastSyncAt;
  if (!ts) {
    label.textContent = "";
    label.title = "";
    return;
  }
  const diffMs = Date.now() - ts;
  const diffMin = Math.round(diffMs / 60000);
  let text;
  if (diffMin < 1) text = "ahora";
  else if (diffMin === 1) text = "hace 1 min";
  else if (diffMin < 60) text = `hace ${diffMin} min`;
  else {
    const h = Math.round(diffMin / 60);
    text = h === 1 ? "hace 1 h" : `hace ${h} h`;
  }
  const full = new Date(ts).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  label.textContent = text;
  label.title = `Última sincronización: ${full}`;
}

/**
 * Filter and render grid
 */
let currentTagVideoId = null;

/**
 * Open Tag Modal for a specific video
 */
function openTagModal(videoId, videoTitle) {
  currentTagVideoId = videoId;
  const modal = document.getElementById("modal-tags");
  const titleEl = document.getElementById("modal-tags-episode-title");
  if (!modal) return;

  if (titleEl) {
    titleEl.textContent = videoTitle || "Episodio";
  }

  renderTagModalList(videoId);
  modal.classList.add("is-open");
}

function closeTagModal() {
  const modal = document.getElementById("modal-tags");
  if (modal) modal.classList.remove("is-open");
  currentTagVideoId = null;
}

function renderTagModalList(videoId) {
  const container = document.getElementById("modal-tags-list");
  if (!container || !videoId) return;

  const currentTags = store.getTags(videoId);
  const allSuggested = store.getAllSuggestedTags();

  container.innerHTML = "";
  allSuggested.forEach((tag) => {
    const isTagged = currentTags.includes(tag);
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = `modal-tag-chip ${isTagged ? "active" : ""}`;
    chip.innerHTML = `<span>${tag}</span> <span class="chip-status-icon">${isTagged ? "✓" : "+"}</span>`;
    chip.onclick = () => {
      store.toggleTag(videoId, tag);
      renderTagModalList(videoId);
      applyFiltersAndRender();
      showToast(isTagged ? `Etiqueta "${tag}" quitada` : `Etiqueta "${tag}" añadida`, "info");
    };
    container.appendChild(chip);
  });
}

/**
 * Load older episodes for subscribed channels
 */
async function handleLoadOlder(btnEl) {
  let originalHtml = "";
  if (btnEl) {
    btnEl.disabled = true;
    btnEl.classList.add("loading");
    originalHtml = btnEl.innerHTML;
    btnEl.innerHTML = `<span class="spinner-small"></span> Cargando más antiguos...`;
  }
  showToast("Buscando episodios más antiguos en YouTube...", "info");

  // Relax restrictive filters if active (e.g. date filter or unheard status filter)
  const updates = {};
  if (store.settings.dateFilter && store.settings.dateFilter !== "all") {
    updates.dateFilter = "all";
  }
  if (store.settings.statusFilter === "unheard") {
    updates.statusFilter = "all";
  }
  if (Object.keys(updates).length > 0) {
    store.updateSettings(updates);
  }

  try {
    const channels = store.getSubscriptions();
    const olderEpisodes = await feedService.fetchAllFeeds(channels, true, true);

    const map = new Map(allEpisodes.map((e) => [e.videoId, e]));
    let newCount = 0;
    for (const ep of olderEpisodes) {
      if (!map.has(ep.videoId)) {
        map.set(ep.videoId, ep);
        newCount++;
      } else {
        const existing = map.get(ep.videoId);
        existing.published = ep.published;
        existing.updated = ep.updated;
        if (ep.duration && !existing.duration) existing.duration = ep.duration;
      }
    }
    allEpisodes = Array.from(map.values());
    allEpisodes.sort((a, b) => new Date(b.published).getTime() - new Date(a.published).getTime());

    applyFiltersAndRender();
    if (newCount > 0) {
      showToast(`¡Cargados ${newCount} episodios anteriores!`, "success");
    } else {
      showToast("No se encontraron más episodios anteriores en estos canales.", "info");
    }
  } catch (err) {
    console.error("Error loading older feeds:", err);
    showToast("No se pudieron cargar más episodios.", "error");
  } finally {
    if (btnEl) {
      btnEl.disabled = false;
      btnEl.classList.remove("loading");
      if (originalHtml) btnEl.innerHTML = originalHtml;
    }
  }
}

/**
 * Filter and render grid
 */
function applyFiltersAndRender() {
  const filtered = filterItems(allEpisodes, store.settings, searchQuery);
  renderEpisodeGrid(filtered, player.currentVideo?.videoId, openTagModal, handleLoadOlder);
  updateFilterDropdowns();
}

function setupFiltersToggle() {
  const toggleBtn = document.getElementById("btn-toggle-filters");
  const panel = document.getElementById("filters-panel");
  if (!toggleBtn || !panel) return;

  const showPanel = (visible) => {
    panel.classList.toggle("hidden", !visible);
    toggleBtn.setAttribute("aria-expanded", String(visible));
  };
  showPanel(store.settings.showFilters);

  toggleBtn.onclick = () => {
    store.settings.showFilters = !store.settings.showFilters;
    store.saveSettings();
    showPanel(store.settings.showFilters);
  };
}

function setupSettingsMenu() {
  const toggleBtn = document.getElementById("btn-settings-menu");
  const menu = document.getElementById("settings-menu-list");
  if (!toggleBtn || !menu) return;

  const setOpen = (open) => {
    menu.classList.toggle("hidden", !open);
    toggleBtn.setAttribute("aria-expanded", String(open));
  };

  toggleBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    setOpen(menu.classList.contains("hidden"));
  });
  menu.addEventListener("click", () => setOpen(false));
  document.addEventListener("click", (e) => {
    if (!menu.contains(e.target)) setOpen(false);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") setOpen(false);
  });
}

/**
 * Setup UI Event Listeners
 */
function setupEventListeners() {
  setupFiltersToggle();
  setupSettingsMenu();

  // Refresh Feed and check for app updates
  const refreshBtn = document.getElementById("btn-refresh-feed");
  if (refreshBtn) {
    refreshBtn.onclick = () => {
      showToast("Actualizando canales...", "info");
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistration().then((reg) => {
          reg?.update().catch(() => {});
        });
      }
      loadFeeds(true);
    };
  }

  // Clear cache button
  const clearCacheBtn = document.getElementById("btn-clear-cache");
  if (clearCacheBtn) {
    clearCacheBtn.onclick = async () => {
      // 1. Clear all feed_* keys from localStorage
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith("feed_")) {
          localStorage.removeItem(key);
        }
      }
      // 2. Clear in-memory cache in feedService
      feedService.memoryCache.clear();
      feedService.lastSyncAt = null;
      // 3. Clear SW Cache API entries for /api/feed
      if ("caches" in window) {
        try {
          const cacheKeys = await caches.keys();
          for (const cacheName of cacheKeys) {
            const cache = await caches.open(cacheName);
            const requests = await cache.keys();
            for (const req of requests) {
              if (new URL(req.url).pathname === "/api/feed") {
                await cache.delete(req);
              }
            }
          }
        } catch (_e) {}
      }
      showToast("Caché limpiada. Recargando episodios...", "success");
      updateSyncLabel();
      loadFeeds(true);
    };
  }

  // Desktop and Mobile Search Inputs (synchronized)
  const searchInput = document.getElementById("search-input");
  const searchClear = document.getElementById("search-clear");
  const mobileSearchInput = document.getElementById("mobile-search-input");
  const mobileSearchClear = document.getElementById("mobile-search-clear");

  function handleSearch(val) {
    searchQuery = val;
    if (searchInput && searchInput.value !== val) searchInput.value = val;
    if (mobileSearchInput && mobileSearchInput.value !== val) mobileSearchInput.value = val;
    if (searchClear) searchClear.classList.toggle("hidden", !val);
    if (mobileSearchClear) mobileSearchClear.classList.toggle("hidden", !val);
    applyFiltersAndRender();
  }

  if (searchInput) {
    searchInput.oninput = (e) => handleSearch(e.target.value);
  }
  if (mobileSearchInput) {
    mobileSearchInput.oninput = (e) => handleSearch(e.target.value);
  }

  if (searchClear) {
    searchClear.onclick = () => handleSearch("");
  }
  if (mobileSearchClear) {
    mobileSearchClear.onclick = () => handleSearch("");
  }

  // --- Compact Filter Dropdowns ---
  const channelSelect = document.getElementById("select-filter-channel");
  if (channelSelect) {
    channelSelect.onchange = (e) => {
      store.updateSettings({ activeChannelId: e.target.value });
      applyFiltersAndRender();
    };
  }

  const dateSelect = document.getElementById("select-filter-date");
  if (dateSelect) {
    dateSelect.onchange = (e) => {
      store.updateSettings({ dateFilter: e.target.value });
      applyFiltersAndRender();
    };
  }

  const durationSelect = document.getElementById("select-filter-duration");
  if (durationSelect) {
    durationSelect.onchange = (e) => {
      store.updateSettings({ durationFilter: e.target.value });
      applyFiltersAndRender();
    };
  }

  const statusSelect = document.getElementById("select-filter-status");
  if (statusSelect) {
    statusSelect.onchange = (e) => {
      store.updateSettings({ statusFilter: e.target.value });
      applyFiltersAndRender();
    };
  }

  const tagSelect = document.getElementById("select-filter-tag");
  if (tagSelect) {
    tagSelect.onchange = (e) => {
      store.updateSettings({ tagFilter: e.target.value });
      applyFiltersAndRender();
    };
  }

  // Reset Filters Bar Button
  const resetBarBtn = document.getElementById("btn-reset-filters-bar");
  if (resetBarBtn) {
    resetBarBtn.onclick = () => {
      store.updateSettings({
        activeChannelId: "all",
        dateFilter: "all",
        durationFilter: "all",
        statusFilter: "unheard",
        tagFilter: "all",
        hideShorts: true,
      });
      applyFiltersAndRender();
      showToast("Filtros restablecidos", "info");
    };
  }

  // Tag Modal Listeners
  const closeTagsBtn = document.getElementById("btn-close-tags");
  const tagsBackdrop = document.getElementById("modal-tags-backdrop");
  if (closeTagsBtn) closeTagsBtn.onclick = closeTagModal;
  if (tagsBackdrop) tagsBackdrop.onclick = closeTagModal;

  const inputNewTag = document.getElementById("input-new-tag");
  const addCustomTagBtn = document.getElementById("btn-add-custom-tag");
  function handleAddTag() {
    if (!currentTagVideoId || !inputNewTag) return;
    const tagText = inputNewTag.value.trim();
    if (tagText) {
      store.addTag(currentTagVideoId, tagText);
      inputNewTag.value = "";
      renderTagModalList(currentTagVideoId);
      applyFiltersAndRender();
      showToast(`Etiqueta "${tagText}" añadida ✓`, "success");
    }
  }
  if (addCustomTagBtn) addCustomTagBtn.onclick = handleAddTag;
  if (inputNewTag) {
    inputNewTag.onkeydown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddTag();
      }
    };
  }

  // Hide Shorts Toggle
  const hideShortsToggle = document.getElementById("toggle-hide-shorts");
  if (hideShortsToggle) {
    hideShortsToggle.checked = store.settings.hideShorts;
    hideShortsToggle.onchange = (e) => {
      store.updateSettings({ hideShorts: e.target.checked });
      applyFiltersAndRender();
      showToast(
        e.target.checked ? "Shorts y clips ocultados" : "Mostrando todos los formatos",
        "info"
      );
    };
  }

  // Status Filter Tabs (legacy compatibility if present)
  const statusTabs = document.querySelectorAll(".status-tab");
  statusTabs.forEach((tab) => {
    const status = tab.dataset.status;
    tab.classList.toggle("active", store.settings.statusFilter === status);
    tab.onclick = () => {
      statusTabs.forEach((t) => t.classList.remove("active"));
      tab.classList.add("active");
      store.updateSettings({ statusFilter: status });
    };
  });

  // Duration Filter Buttons (legacy compatibility if present)
  const durationBtns = document.querySelectorAll(".duration-pill");
  durationBtns.forEach((btn) => {
    const durVal = btn.dataset.duration;
    btn.classList.toggle("active", store.settings.durationFilter === durVal);
    btn.onclick = () => {
      durationBtns.forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      store.updateSettings({ durationFilter: durVal });
    };
  });

  // Player Dock Controls
  const dockPlay = document.getElementById("player-dock-play");
  if (dockPlay) dockPlay.onclick = () => player.togglePlayPause();

  const dockRewind = document.getElementById("player-dock-rewind");
  if (dockRewind) dockRewind.onclick = () => player.skip(-15);

  const dockForward = document.getElementById("player-dock-forward");
  if (dockForward) dockForward.onclick = () => player.skip(15);

  const dockSpeed = document.getElementById("player-dock-speed");
  if (dockSpeed) dockSpeed.onclick = () => player.cycleSpeed();

  const dockScrubber = document.getElementById("player-scrub-slider");
  if (dockScrubber) {
    dockScrubber.oninput = (e) => {
      if (player.duration > 0) {
        const targetSec = (e.target.value / 100) * player.duration;
        player.seek(targetSec);
      }
    };
  }

  const dockListened = document.getElementById("player-dock-listened");
  if (dockListened) {
    dockListened.onclick = () => {
      if (player.currentVideo) {
        const nowListened = store.toggleListened(player.currentVideo.videoId, player.duration);
        showToast(
          nowListened ? "Episodio marcado como escuchado ✓" : "Marcado como pendiente",
          "success"
        );
      }
    };
  }

  const dockExpand = document.getElementById("player-dock-expand");
  const closeTheater = document.getElementById("btn-close-theater");
  if (dockExpand) dockExpand.onclick = () => player.toggleExpand();
  if (closeTheater) closeTheater.onclick = () => player.toggleExpand();

  // Sleep Timer dropdown in theater
  const sleepTimerSelect = document.getElementById("theater-sleep-timer");
  if (sleepTimerSelect) {
    sleepTimerSelect.onchange = (e) => {
      const mins = parseInt(e.target.value, 10);
      player.setSleepTimer(mins);
      if (mins > 0) {
        showToast(`Temporizador fijado: ${mins} minutos`, "info");
      }
    };
  }

  // Modals management
  setupModals();
}

function parseMinutes(value) {
  const minutes = parseFloat(value);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}

/**
 * Modals setup
 */
function setupModals() {
  // Channels Modal
  const modalChannels = document.getElementById("modal-channels");
  const btnOpenChannels = document.getElementById("btn-open-channels");
  const btnCloseChannels = document.getElementById("btn-close-channels");
  const formAddChannel = document.getElementById("form-add-channel");
  const channelListEl = document.getElementById("channels-manager-list");
  const btnResetChannels = document.getElementById("btn-reset-channels");

  function renderChannelsManagerList() {
    if (!channelListEl) return;
    const subs = store.getSubscriptions();
    const countBadge = document.getElementById("sources-count-badge");
    const activeSubs = subs.filter((s) => s.enabled !== false);
    if (countBadge) {
      countBadge.textContent = `${activeSubs.length} activa${activeSubs.length === 1 ? "" : "s"} (${subs.length} total)`;
    }

    channelListEl.innerHTML = "";

    subs.forEach((sub) => {
      const isEnabled = sub.enabled !== false;
      const durFilter = store.getChannelDurationFilter(sub.id);
      const row = document.createElement("div");
      row.className = `channel-manager-row ${isEnabled ? "" : "is-paused"}`;
      row.innerHTML = `
        <div class="channel-row-info">
          <span class="channel-dot" style="background: ${sub.color || "#6366f1"};"></span>
          <div>
            <strong>${sub.title}</strong>
            <span class="channel-handle">${sub.handle || sub.host || ""}</span>
          </div>
        </div>
        <div class="channel-row-actions">
          <label class="toggle-switch-wrapper" title="${isEnabled ? "Pausar fuente" : "Activar fuente"}">
            <input type="checkbox" class="toggle-channel-enabled" ${isEnabled ? "checked" : ""} data-id="${sub.id}">
            <span class="toggle-slider"></span>
            <span class="channel-status-label">${isEnabled ? "Activo" : "Pausado"}</span>
          </label>
          ${
            sub.fromConfig
              ? ""
              : `<button class="btn-icon btn-delete-channel" title="Dar de baja definitiva" data-id="${sub.id}">
            <svg viewBox="0 0 24 24" class="icon"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
          </button>`
          }
        </div>
        <div class="channel-row-duration">
          <span>Duración (min)</span>
          <input type="number" min="0" step="1" inputmode="numeric" class="input-duration-min"
            placeholder="mín." value="${durFilter.minMin ?? ""}" aria-label="Duración mínima en minutos">
          <span>–</span>
          <input type="number" min="0" step="1" inputmode="numeric" class="input-duration-max"
            placeholder="máx." value="${durFilter.maxMin ?? ""}" aria-label="Duración máxima en minutos">
        </div>
      `;

      const minInput = row.querySelector(".input-duration-min");
      const maxInput = row.querySelector(".input-duration-max");
      const saveDurationFilter = () => {
        store.updateChannelDurationFilter(
          sub.id,
          parseMinutes(minInput.value),
          parseMinutes(maxInput.value)
        );
        applyFiltersAndRender();
      };
      minInput.onchange = saveDurationFilter;
      maxInput.onchange = saveDurationFilter;

      // Toggle Active / Paused
      const toggle = row.querySelector(".toggle-channel-enabled");
      toggle.onchange = () => {
        const nowEnabled = store.toggleSubscriptionEnabled(sub.id);
        renderChannelsManagerList();
        renderPopularSuggestions();
        renderChannelChips(
          store.getSubscriptions(),
          store.settings.activeChannelId,
          onChannelSelect
        );
        applyFiltersAndRender();
        showToast(
          nowEnabled ? `Fuente "${sub.title}" activada ✓` : `Fuente "${sub.title}" pausada`,
          nowEnabled ? "success" : "info"
        );
      };

      // Delete (Dar de baja definitiva)
      const deleteButton = row.querySelector(".btn-delete-channel");
      if (deleteButton)
        deleteButton.onclick = () => {
          if (confirm(`¿Dar de baja y eliminar la fuente "${sub.title}"?`)) {
            store.removeSubscription(sub.id);
            renderChannelsManagerList();
            renderPopularSuggestions();
            renderChannelChips(
              store.getSubscriptions(),
              store.settings.activeChannelId,
              onChannelSelect
            );
            loadFeeds(true);
            showToast(`Fuente "${sub.title}" dada de baja`, "info");
          }
        };

      channelListEl.appendChild(row);
    });
  }

  function renderPopularSuggestions() {
    const container = document.getElementById("popular-suggestions-list");
    if (!container) return;
    container.innerHTML = "";
    const currentSubs = store.getSubscriptions();

    POPULAR_SUGGESTIONS.forEach((sug) => {
      const isAdded = currentSubs.some((s) => s.id === sug.id);
      const item = document.createElement("div");
      item.className = `suggestion-card ${isAdded ? "is-added" : ""}`;
      item.innerHTML = `
        <div class="sug-info">
          <span class="channel-dot" style="background: ${sug.color}"></span>
          <div>
            <strong>${sug.title}</strong>
            <span class="sug-desc">${sug.category}</span>
          </div>
        </div>
        <button class="btn btn-sm ${isAdded ? "btn-secondary" : "btn-primary"} btn-add-sug" ${isAdded ? "disabled" : ""}>
          ${isAdded ? "✓ Añadido" : "+ Añadir"}
        </button>
      `;

      if (!isAdded) {
        item.querySelector(".btn-add-sug").onclick = () => {
          store.addSubscription(sug);
          renderChannelsManagerList();
          renderPopularSuggestions();
          renderChannelChips(
            store.getSubscriptions(),
            store.settings.activeChannelId,
            onChannelSelect
          );
          loadFeeds(true);
          showToast(`¡Fuente "${sug.title}" añadida!`, "success");
        };
      }

      container.appendChild(item);
    });
  }

  if (btnOpenChannels) {
    btnOpenChannels.onclick = () => {
      renderChannelsManagerList();
      renderPopularSuggestions();
      modalChannels.classList.add("is-open");
    };
  }
  if (btnCloseChannels) {
    btnCloseChannels.onclick = () => modalChannels.classList.remove("is-open");
  }

  if (formAddChannel) {
    formAddChannel.onsubmit = async (e) => {
      e.preventDefault();
      const input = document.getElementById("input-new-channel");
      const val = input.value.trim();
      if (!val) return;

      const submitBtn = formAddChannel.querySelector('button[type="submit"]');
      submitBtn.disabled = true;
      submitBtn.textContent = "Buscando...";

      try {
        const resolved = await feedService.resolveChannel(val);
        if (resolved && resolved.channelId) {
          const added = store.addSubscription({
            id: resolved.channelId,
            title: resolved.title || val,
            host: resolved.host || "",
            handle: resolved.handle || (val.startsWith("@") ? val : `@${val}`),
            avatar: resolved.avatar || "",
            color: resolved.color || (resolved.type === "substack" ? "#ff6719" : "#8b5cf6"),
            type: resolved.type || "youtube",
            feedUrl: resolved.feedUrl,
          });

          if (added) {
            input.value = "";
            renderChannelsManagerList();
            renderChannelChips(
              store.getSubscriptions(),
              store.settings.activeChannelId,
              onChannelSelect
            );
            loadFeeds(true);
            const typeLabel =
              resolved.type === "substack"
                ? "Publicación Substack"
                : resolved.type === "rss"
                  ? "Podcast RSS"
                  : "Canal";
            showToast(`¡${typeLabel} "${resolved.title || val}" añadido!`, "success");
          } else {
            showToast("La fuente ya estaba en tu lista", "info");
          }
        } else {
          showToast("No se encontró el canal o feed. Verifica el @handle o URL.", "error");
        }
      } catch (_err) {
        showToast("Error al resolver el canal", "error");
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = "Añadir";
      }
    };
  }

  if (btnResetChannels) {
    btnResetChannels.onclick = () => {
      if (
        confirm(
          "¿Restablecer canales a los 3 predeterminados (Kaizen, Tengo un Plan, Nadie Sabe Nada)?"
        )
      ) {
        store.resetSubscriptions();
        renderChannelsManagerList();
        renderChannelChips(
          store.getSubscriptions(),
          store.settings.activeChannelId,
          onChannelSelect
        );
        loadFeeds(true);
        showToast("Canales restablecidos", "success");
      }
    };
  }

  // Brave Tips Modal
  const modalBrave = document.getElementById("modal-brave");
  const btnOpenBrave = document.getElementById("btn-open-brave-tips");
  const btnCloseBrave = document.getElementById("btn-close-brave");
  if (btnOpenBrave) btnOpenBrave.onclick = () => modalBrave.classList.add("is-open");
  if (btnCloseBrave) btnCloseBrave.onclick = () => modalBrave.classList.remove("is-open");

  // Backup Modal
  const modalBackup = document.getElementById("modal-backup");
  const btnOpenBackup = document.getElementById("btn-open-backup");
  const btnCloseBackup = document.getElementById("btn-close-backup");
  const btnExport = document.getElementById("btn-export-backup");
  const btnImport = document.getElementById("btn-import-backup");
  const backupTextarea = document.getElementById("textarea-backup");

  if (btnOpenBackup) {
    btnOpenBackup.onclick = () => {
      if (backupTextarea) backupTextarea.value = store.exportBackup();
      modalBackup.classList.add("is-open");
    };
  }
  if (btnCloseBackup) btnCloseBackup.onclick = () => modalBackup.classList.remove("is-open");

  if (btnExport) {
    btnExport.onclick = () => {
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(store.exportBackup());
      const downloadAnchor = document.createElement("a");
      downloadAnchor.setAttribute("href", dataStr);
      downloadAnchor.setAttribute(
        "download",
        `podtube_backup_${new Date().toISOString().slice(0, 10)}.json`
      );
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();
      showToast("Copia descargada ✓", "success");
    };
  }

  if (btnImport) {
    btnImport.onclick = () => {
      if (!backupTextarea) return;
      const ok = store.importBackup(backupTextarea.value);
      if (ok) {
        showToast("Copia de seguridad restaurada ✓", "success");
        modalBackup.classList.remove("is-open");
        renderChannelChips(
          store.getSubscriptions(),
          store.settings.activeChannelId,
          onChannelSelect
        );
        loadFeeds(true);
      } else {
        showToast("El JSON de la copia no es válido", "error");
      }
    };
  }

  // Close modals on backdrop click
  document.querySelectorAll(".modal-backdrop").forEach((backdrop) => {
    backdrop.onclick = (e) => {
      if (e.target === backdrop) {
        backdrop.closest(".modal").classList.remove("is-open");
      }
    };
  });
}

function onChannelSelect(channelId) {
  store.updateSettings({ activeChannelId: channelId });
  renderChannelChips(store.getSubscriptions(), channelId, onChannelSelect);
  applyFiltersAndRender();
}

/**
 * Bootstrap Application
 */
function initApp() {
  initServiceWorker();
  setupEventListeners();

  // Initial render of chips
  renderChannelChips(store.getSubscriptions(), store.settings.activeChannelId, onChannelSelect);

  // Subscribe to changes in store
  store.subscribe(() => {
    renderChannelChips(store.getSubscriptions(), store.settings.activeChannelId, onChannelSelect);
    applyFiltersAndRender();
    updatePlayerDock(player);
  });

  // Subscribe to player updates (dock timeline updates; cards updated in-place without rebuilding DOM)
  let lastTrackId = null;
  player.subscribe(() => {
    updatePlayerDock(player);
    const currentId = player.currentVideo?.videoId || null;
    if (currentId !== lastTrackId) {
      lastTrackId = currentId;
      updateActiveCardState(currentId);
    }
  });

  const nowPlaying = store.getNowPlaying();
  if (nowPlaying) player.restore(nowPlaying);

  // Automatically ensure background play resumes if browser paused on visibility change
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      if (player.isPlaying && player.player) {
        try {
          const state =
            typeof player.player.getPlayerState === "function"
              ? player.player.getPlayerState()
              : -1;
          if (state === 2 /* PAUSED */) {
            player.player.playVideo();
          }
        } catch {}
      }
    }
  });

  // Initial feed load
  loadFeeds();
}

document.addEventListener("DOMContentLoaded", initApp);
